import React from 'react';

/**
 * Staff ID Card (CR80 size: 54mm x 85.6mm)
 * Reusable for both preview modal and dedicated print page.
 * All dimensions in mm so preview == print == final output.
 *
 * Props:
 *  - staff: { name, role, department, phone, email, photo, employee_id }
 *  - center: { center_name, center_code }
 *  - scale: display-only zoom (CSS transform) for preview modal.
 *           Print view must ALWAYS pass scale=1.
 */
export const StaffIdCard = ({ staff, center, scale = 1 }) => {
  if (!staff) return null;
  // Brand fallback when the center has not uploaded its own logo yet.
  const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_skill-india-partner/artifacts/h002c2co_1%20copy.png";
  const logoSrc = (center && (center.logo_url || center.logo)) || FALLBACK_LOGO;
  const initial = (staff.name || '?').charAt(0).toUpperCase();

  // Display-only formatting: slug → Title Case (NEVER cross-map to a different role).
  const formatRoleSlug = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return 'Staff';
    if (/[A-Z]/.test(s) && !s.includes('_')) return s;
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const roleLabel = formatRoleSlug(staff.role);

  return (
    <div
      className="iqra-staff-card"
      style={{
        // CR80 card physical dimensions
        width: '54mm',
        height: '85.6mm',
        position: 'relative',
        backgroundColor: '#ffffff',
        borderRadius: '3mm',
        overflow: 'hidden',
        boxSizing: 'border-box',
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
        color: '#0f2942',
        boxShadow: '0 4px 16px rgba(15,41,66,0.18)',
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      {/* Curved navy header */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '27mm',
          backgroundColor: '#0f2942',
          borderBottomLeftRadius: '50% 9mm',
          borderBottomRightRadius: '50% 9mm',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <p
          style={{
            margin: 0,
            padding: '4mm 3mm 0',
            textAlign: 'center',
            color: '#ffffff',
            fontSize: '3.4mm',
            fontWeight: 800,
            letterSpacing: '0.3mm',
            textTransform: 'uppercase',
            lineHeight: 1.15,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {center?.center_name || center?.name || 'Training Center'}
        </p>
        <p
          style={{
            margin: '1.5mm 0 0 0',
            textAlign: 'center',
            color: '#93c5fd',
            fontSize: '2mm',
            fontWeight: 600,
            letterSpacing: '0.6mm',
            textTransform: 'uppercase',
          }}
        >
          Staff Identity Card
        </p>
      </div>

      {/* Circular photo overlapping header */}
      <div
        style={{
          position: 'absolute',
          top: '13mm',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '28mm',
          height: '28mm',
          borderRadius: '50%',
          border: '1mm solid #0f2942',
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
          boxShadow: '0 2px 6px rgba(15,41,66,0.25)',
          zIndex: 3,
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        {staff.photo ? (
          <img
            src={staff.photo}
            alt="Staff"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span style={{ fontSize: '12mm', fontWeight: 'bold', color: '#0f2942' }}>{initial}</span>
        )}
      </div>

      {/* Details body */}
      <div
        style={{
          position: 'absolute',
          top: '43mm',
          left: 0,
          right: 0,
          bottom: '13mm',
          padding: '0 4mm',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <p
          style={{
            margin: 0,
            textAlign: 'center',
            fontSize: '3.8mm',
            fontWeight: 800,
            color: '#0f2942',
            textTransform: 'uppercase',
            letterSpacing: '0.2mm',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {staff.name}
        </p>

        {/* Role pill */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5mm' }}>
          <span
            style={{
              display: 'inline-block',
              backgroundColor: '#1d4ed8',
              color: '#ffffff',
              fontSize: '2.3mm',
              fontWeight: 700,
              padding: '0.8mm 3.5mm',
              borderRadius: '999px',
              textTransform: 'capitalize',
              letterSpacing: '0.1mm',
              WebkitPrintColorAdjust: 'exact',
              printColorAdjust: 'exact',
            }}
          >
            {roleLabel}
          </span>
        </div>

        {/* Info rows */}
        <div
          style={{
            marginTop: '2.5mm',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.3mm',
            textAlign: 'left',
          }}
        >
          <InfoRow label="ID No" value={staff.employee_id || '—'} />
          {staff.email && <InfoRow label="Email" value={staff.email} />}
          {staff.phone && <InfoRow label="Phone" value={staff.phone} />}
          {staff.department && <InfoRow label="Dept." value={staff.department} />}
        </div>
      </div>

      {/* Footer: Iqra Skills logo (no white tile) + center name + code */}
      <div
        style={{
          position: 'absolute',
          left: 0, right: 0, bottom: 0,
          height: '13mm',
          backgroundColor: '#0f2942',
          display: 'flex',
          alignItems: 'center',
          padding: '0 3mm',
          gap: '2.5mm',
          boxSizing: 'border-box',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <img
          src={logoSrc}
          alt="Center logo"
          style={{ width: '11mm', height: '11mm', objectFit: 'contain', flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: '2.4mm',
              color: '#ffffff',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.1mm',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {center?.center_name || center?.name || 'Iqra Skills'}
          </p>
          <p
            style={{
              margin: '0.7mm 0 0 0',
              fontSize: '2.1mm',
              color: '#93c5fd',
              fontWeight: 700,
              letterSpacing: '0.15mm',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {center?.center_code || '—'}
          </p>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', fontSize: '2.6mm', color: '#0f2942', lineHeight: 1.2 }}>
    <span style={{ fontWeight: 800, width: '13mm', flexShrink: 0 }}>{label}</span>
    <span style={{ fontWeight: 800 }}>:</span>
    <span
      style={{
        marginLeft: '1.2mm',
        fontWeight: 500,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </div>
);

export default StaffIdCard;
